import { TransactionTypesEnum } from '@interfaces/models';
import { SettlementFundAoAEnum } from '@interfaces/settlement-fund/settlement-fund.enum';
import { createSelector } from '@ngrx/store';
import { getOpenAccountState } from '@store/index';
import { InternalTransferState } from '@store/reducers/internal-transfer/internal-transfer.reducer';
import { IOpenAccountState } from '@store/reducers/open-account/open-account.reducer';
import {
  selectInternalTransferHasAvgCostHoldings,
  selectInternalTransferState,
} from '@store/selectors/internal-transfer/internal-transfer.selectors';
import { selectSettlementFundChoice } from '@store/selectors/open-account/open-account.selectors';

import {
  selectEmploymentReceipt,
  selectFinancialProfileReceipt,
  selectProfileReceipt,
} from '../new-account/new-account.selectors';
import { selectIsDivorceFlow } from './../../../selectors/triage/triage.selectors';
import {
  buildAccountFromInternalTransfer,
  buildAccountToInternalTransfer,
} from './cashier-internal-transfer';

export const selectAccountFromInternalTransferReceipt = (editable: boolean) => {
  return createSelector(
    selectInternalTransferState,
    selectInternalTransferHasAvgCostHoldings,
    selectIsDivorceFlow,
    (
      state: InternalTransferState,
      hasAvgCostHoldings: boolean | null,
      isDivorceTransfer: boolean,
    ) =>
      buildAccountFromInternalTransfer(
        state,
        editable,
        isDivorceTransfer,
        hasAvgCostHoldings ?? false,
      ),
  );
};

export const selectAccountToInternalTransferReceipt = (editable: boolean) => {
  return createSelector(
    selectInternalTransferState,
    getOpenAccountState,
    selectSettlementFundChoice,
    selectIsDivorceFlow,
    (
      state: InternalTransferState,
      openAccountState: IOpenAccountState,
      settlementFundChoice: SettlementFundAoAEnum | null,
      isDivorceTransfer: boolean,
    ) =>
      buildAccountToInternalTransfer(
        state,
        openAccountState,
        editable,
        settlementFundChoice,
        isDivorceTransfer,
      ),
  );
};

export const selectInternalTransferReceiptData = (editable: boolean = true) => {
  return createSelector(
    selectAccountFromInternalTransferReceipt(editable),
    selectAccountToInternalTransferReceipt(editable),
    selectProfileReceipt(editable),
    selectEmploymentReceipt(editable),
    selectFinancialProfileReceipt(editable),
    selectIsDivorceFlow,
    selectInternalTransferState,
    (from, to, profile, employment, financial, isDivorceTransfer, itState) => {
      const isNewAccount =
        itState.transactionType === TransactionTypesEnum.NEW_ACCOUNT ||
        itState.transactionType === TransactionTypesEnum.FORCED_NEW_ACCOUNT;

      // Only show profile/employment/financial sections for new account transfers
      // Existing account transfers (including divorce) only show from/to
      if (isDivorceTransfer || !isNewAccount) {
        return {
          from,
          to,
        };
      }

      // New account transfers show all 5 sections
      return {
        from,
        to,
        profile,
        employment,
        financial,
      };
    },
  );
};
